import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Box,
  Tooltip,
  Divider,
  Collapse,
} from '@mui/material';
import DashboardIcon from '@mui/icons-material/Dashboard';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import RequestQuoteIcon from '@mui/icons-material/RequestQuote';
import SellIcon from '@mui/icons-material/Sell';
import BookmarkIcon from '@mui/icons-material/Bookmark';
import EditNoteIcon from '@mui/icons-material/EditNote';
import AssessmentIcon from '@mui/icons-material/Assessment';
import TrackChangesIcon from '@mui/icons-material/TrackChanges';
import ApprovalIcon from '@mui/icons-material/Approval';
import BarChartIcon from '@mui/icons-material/BarChart';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DescriptionIcon from '@mui/icons-material/Description';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import StorefrontIcon from '@mui/icons-material/Storefront';
import ListAltIcon from '@mui/icons-material/ListAlt';
import TuneIcon from '@mui/icons-material/Tune';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import InventoryIcon from '@mui/icons-material/Inventory';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import BadgeIcon from '@mui/icons-material/Badge';
import BoltIcon from '@mui/icons-material/Bolt';
import PeopleIcon from '@mui/icons-material/People';
import CalculateIcon from '@mui/icons-material/Calculate';
import EngineeringIcon from '@mui/icons-material/Engineering';
import StarsIcon from '@mui/icons-material/Stars';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PlaylistAddCheckIcon from '@mui/icons-material/PlaylistAddCheck';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import DomainIcon from '@mui/icons-material/Domain';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import RedeemIcon from '@mui/icons-material/Redeem';
import CardGiftcardIcon from '@mui/icons-material/CardGiftcard';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import WorkspacePremiumIcon from '@mui/icons-material/WorkspacePremium';
import ArticleIcon from '@mui/icons-material/Article';
import GavelIcon from '@mui/icons-material/Gavel';
import FeedbackIcon from '@mui/icons-material/Feedback';
import CampaignIcon from '@mui/icons-material/Campaign';
import PaidIcon from '@mui/icons-material/Paid';
import InsightsIcon from '@mui/icons-material/Insights';
import ViewColumnIcon from '@mui/icons-material/ViewColumn';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SchoolIcon from '@mui/icons-material/School';
import PlayCircleIcon from '@mui/icons-material/PlayCircle';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import QuizIcon from '@mui/icons-material/Quiz';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import ViewKanbanIcon from '@mui/icons-material/ViewKanban';
import ShareIcon from '@mui/icons-material/Share';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import AppsIcon from '@mui/icons-material/Apps';
import PersonIcon from '@mui/icons-material/Person';
import WallpaperIcon from '@mui/icons-material/Wallpaper';
import WorkOutlineIcon from '@mui/icons-material/WorkOutline';
import LibraryBooksIcon from '@mui/icons-material/LibraryBooks';
import SpaceDashboardIcon from '@mui/icons-material/SpaceDashboard';
import FolderOpenIcon from '@mui/icons-material/FolderOpen';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HubIcon from '@mui/icons-material/Hub';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import AutoStoriesIcon from '@mui/icons-material/AutoStories';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import LiveTvIcon from '@mui/icons-material/LiveTv';
import EventIcon from '@mui/icons-material/Event';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import RecordVoiceOverIcon from '@mui/icons-material/RecordVoiceOver';
import ExpandLess from '@mui/icons-material/ExpandLess';
import ExpandMore from '@mui/icons-material/ExpandMore';
import { NAV_MODULES } from '@/utils/constants';
import type { NavModule } from '@/utils/constants';
import { useAuthStore } from '@/stores/authStore';

/** 图标映射 */
const ICON_MAP: Record<string, React.ReactElement> = {
  Dashboard: <DashboardIcon />,
  AccountTree: <AccountTreeIcon />,
  ReceiptLong: <ReceiptLongIcon />,
  RequestQuote: <RequestQuoteIcon />,
  Sell: <SellIcon />,
  Bookmark: <BookmarkIcon />,
  EditNote: <EditNoteIcon />,
  Assessment: <AssessmentIcon />,
  TrackChanges: <TrackChangesIcon />,
  Approval: <ApprovalIcon />,
  BarChart: <BarChartIcon />,
  Settings: <SettingsIcon />,
  HelpOutline: <HelpOutlineIcon />,
  Description: <DescriptionIcon />,
  SwapHoriz: <SwapHorizIcon />,
  FileUpload: <FileUploadIcon />,
  Storefront: <StorefrontIcon />,
  AttachMoney: <AttachMoneyIcon />,
  ListAlt: <ListAltIcon />,
  Tune: <TuneIcon />,
  Inventory: <InventoryIcon />,
  Inventory2: <Inventory2Icon />,
  Badge: <BadgeIcon />,
  Bolt: <BoltIcon />,
  People: <PeopleIcon />,
  Calculate: <CalculateIcon />,
  Engineering: <EngineeringIcon />,
  Stars: <StarsIcon />,
  AccountBalance: <AccountBalanceIcon />,
  PlaylistAddCheck: <PlaylistAddCheckIcon />,
  AdminPanelSettings: <AdminPanelSettingsIcon />,
  Domain: <DomainIcon />,
  EmojiEvents: <EmojiEventsIcon />,
  Redeem: <RedeemIcon />,
  CardGiftcard: <CardGiftcardIcon />,
  SupervisorAccount: <SupervisorAccountIcon />,
  MailOutline: <MailOutlineIcon />,
  WorkspacePremium: <WorkspacePremiumIcon />,
  Article: <ArticleIcon />,
  Gavel: <GavelIcon />,
  Feedback: <FeedbackIcon />,
  Campaign: <CampaignIcon />,
  Paid: <PaidIcon />,
  Insights: <InsightsIcon />,
  Event: <EventIcon />,
  ViewColumn: <ViewColumnIcon />,
  Notifications: <NotificationsIcon />,
  School: <SchoolIcon />,
  PlayCircle: <PlayCircleIcon />,
  VideoLibrary: <VideoLibraryIcon />,
  Quiz: <QuizIcon />,
  VerifiedUser: <VerifiedUserIcon />,
  ViewKanban: <ViewKanbanIcon />,
  Share: <ShareIcon />,
  PersonAdd: <PersonAddIcon />,
  Apps: <AppsIcon />,
  Person: <PersonIcon />,
  Wallpaper: <WallpaperIcon />,
  WorkOutline: <WorkOutlineIcon />,
  LibraryBooks: <LibraryBooksIcon />,
  SpaceDashboard: <SpaceDashboardIcon />,
  FolderOpen: <FolderOpenIcon />,
  AccessTime: <AccessTimeIcon />,
  Hub: <HubIcon />,
  WarningAmber: <WarningAmberIcon />,
  AutoAwesome: <AutoAwesomeIcon />,
  LiveTv: <LiveTvIcon />,
  MenuBook: <MenuBookIcon />,
  AutoStories: <AutoStoriesIcon />,
  LocalShipping: <LocalShippingIcon />,
  RecordVoiceOver: <RecordVoiceOverIcon />,
};

const DRAWER_WIDTH = 240;

interface SidebarProps {
  width: number;
  collapsed: boolean;
  /** 移动端模式 */
  mobileOpen?: boolean;
  /** 关闭移动端抽屉 */
  onMobileClose?: () => void;
}

const Sidebar: React.FC<SidebarProps> = ({ width, collapsed, mobileOpen, onMobileClose }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const currentRole = useAuthStore((s) => s.currentRole);
  const isPlatformAdmin = useAuthStore((s) => s.isPlatformAdmin);
  const isChannel = useAuthStore((s) => s.isChannel);
  const isConsultant = useAuthStore((s) => s.isConsultant);
  const identity = useAuthStore((s) => s.identity);
  const [expanded, setExpanded] = useState<Record<string, boolean>>({ account: true });

  /** 根据当前角色判断菜单项是否可见（requiredRoles 为空表示所有登录用户可见） */
  const isItemVisible = (mod: NavModule): boolean => {
    if (!mod.requiredRoles || mod.requiredRoles.length === 0) return true;
    if (currentRole && mod.requiredRoles.includes(currentRole)) return true;
    // 认证咨询师：不属于租户角色体系，单独判定（商学院创作端等）
    if (isConsultant && mod.requiredRoles.includes('consultant')) return true;
    // 咨询师身份用户（无论认证状态）：用于 KYC 自助提交页，使未通过认证者也能进入页面提交/查看进度。
    if (identity === 'consultant' && mod.requiredRoles.includes('consultant_self')) return true;
    // 平台管理员（任一租户持有 platform_admin）全局可见平台级菜单项，
    // 不受当前选中租户角色限制（切到非平台租户也不丢菜单）。
    if (isPlatformAdmin && mod.requiredRoles.includes("platform_admin")) return true;
    // 授权渠道：平台管理员授权的指定账号，可见 requireChannel 标记的菜单项
    // （当前即「利润自测线索」，数据域仅限该渠道的直接下线）。
    if (isChannel && mod.requireChannel) return true;
    return false;
  };

  /** 过滤菜单：父级菜单需保留至少一个可见子项才显示 */
  const filterModules = (mods: NavModule[]): NavModule[] =>
    mods
      .filter(isItemVisible)
      .map((mod) => {
        if (!mod.children || mod.children.length === 0) return mod;
        const children = filterModules(mod.children);
        return children.length > 0 ? { ...mod, children } : mod;
      })
      .filter((mod) => !mod.children || mod.children.length > 0);

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    // 路径段边界匹配：避免 /account/quotation 误匹配 /account/quotation-board 等兄弟路由
    return location.pathname === path || location.pathname.startsWith(path + '/');
  };

  /** 判断父级菜单是否有任意子孙项处于活跃状态（递归，支持三级嵌套） */
  const hasActiveChild = (mod: NavModule): boolean => {
    if (!mod.children) return false;
    return mod.children.some(child => isActive(child.path) || hasActiveChild(child));
  };

  /** 渲染单个菜单项 */
  const renderItem = (mod: NavModule, depth: number = 0) => {
    const active = isActive(mod.path);
    const childActive = hasActiveChild(mod);
    const icon = ICON_MAP[mod.icon] || ICON_MAP[mod.key];
    const hasChildren = mod.children && mod.children.length > 0;
    const open = expanded[mod.key] ?? (hasChildren && childActive);

    const button = (
      <ListItemButton
        onClick={() => {
          if (hasChildren) {
            setExpanded(prev => ({ ...prev, [mod.key]: !prev[mod.key] }));
          } else if (mod.enabled) {
            if (mod.external) {
              // 外链：整页跳转（SPA 之外的静态页，如 /poster/*.html）
              window.location.href = mod.path;
            } else {
              navigate(mod.path);
            }
            // 移动端导航后关闭侧边栏
            onMobileClose?.();
          }
        }}
        selected={(!hasChildren && active) || (hasChildren && childActive)}
        disabled={!mod.enabled}
        sx={{
          mx: 1, borderRadius: 1, mb: 0.5,
          pl: depth > 0 ? 1 + depth * 2 : 1,
          '&.Mui-selected': { backgroundColor: '#E8EDF5', color: '#1A365D', '&:hover': { backgroundColor: '#D5DDE9' } },
          '&.Mui-disabled': { opacity: 0.4 },
        }}
      >
        <ListItemIcon sx={{ minWidth: collapsed ? 0 : 36, color: 'inherit' }}>{icon}</ListItemIcon>
        {!collapsed && <ListItemText primary={mod.label} primaryTypographyProps={{ fontSize: 14 }} />}
        {!collapsed && hasChildren && (open ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />)}
      </ListItemButton>
    );

    const rendered = collapsed ? (
      <Tooltip key={mod.key} title={mod.label} placement="right">{button}</Tooltip>
    ) : (
      <React.Fragment key={mod.key}>{button}</React.Fragment>
    );

    if (!hasChildren) return rendered;

    return (
      <React.Fragment key={mod.key}>
        {rendered}
        {!collapsed && (
          <Collapse in={open} timeout="auto" unmountOnExit>
            <List disablePadding>
              {mod.children!.map(child => renderItem(child, depth + 1))}
            </List>
          </Collapse>
        )}
      </React.Fragment>
    );
  };

  /** 渲染侧边栏内容（共用） */
  const drawerContent = (
    <>
      {/* Logo 区域 */}
      <Box
        sx={{
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: collapsed ? 'center' : 'flex-start',
          px: collapsed ? 0 : 2,
          borderBottom: '1px solid #e0e0e0',
        }}
      >
        {collapsed ? (
          <Box
            component="img"
            src="/logo-icon.svg"
            alt="芸胞 yuncell"
            sx={{ width: 32, height: 32 }}
          />
        ) : (
          <Box
            component="img"
            src="/logo.svg"
            alt="芸胞 yuncell"
            sx={{ height: 36 }}
          />
        )}
      </Box>

      {/* 导航列表 */}
      <List sx={{ pt: 1 }}>
        {filterModules(NAV_MODULES).map((mod: NavModule) => renderItem(mod))}
      </List>
    </>
  );

  // 移动端：临时 Drawer（overlay + 关闭按钮）
  if (mobileOpen !== undefined) {
    return (
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={onMobileClose}
        ModalProps={{ keepMounted: true }} // 提升移动端性能
        sx={{
          width: DRAWER_WIDTH,
          flexShrink: 0,
          '& .MuiDrawer-paper': {
            width: DRAWER_WIDTH,
            boxSizing: 'border-box',
          },
        }}
      >
        {drawerContent}
      </Drawer>
    );
  }

  // 桌面端：永久 Drawer
  return (
    <Drawer
      variant="permanent"
      sx={{
        width,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width,
          boxSizing: 'border-box',
          transition: 'width 0.2s ease',
          overflowX: 'hidden',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default Sidebar;
